import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import css from './HorizontalCarousel.css';

// Helper to check if we're in browser environment
const isBrowser = typeof window !== 'undefined';

const HorizontalCarousel = props => {
  const { children, className } = props;
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const containerRef = useRef(null);
  
  // Set isClient to true on mount
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Calculate total pages based on container width and number of items
  useEffect(() => {
    if (!isClient) return; // Skip calculation during SSR

    const calculatePages = () => {
      if (containerRef.current) {
        const container = containerRef.current;
        const containerWidth = container.clientWidth;
        const itemCount = React.Children.count(children);
        
        // Get the width of a single item (assuming all items have similar width)
        let itemWidth = 0;
        if (container.firstChild && container.firstChild.offsetWidth) {
          itemWidth = container.firstChild.offsetWidth;
        } else {
          // Default to approximate values based on screen size
          itemWidth = isBrowser && window.innerWidth < 768 ? 160 : 280;
        }
        
        // Calculate how many items fit in the container
        const itemsPerPage = Math.max(1, Math.floor(containerWidth / itemWidth));
        
        // Calculate total pages
        const pages = Math.ceil(itemCount / itemsPerPage);
        setTotalPages(pages);
        setHasOverflow(pages > 1);
        
        // Reset current page if it's now out of bounds
        if (currentPage >= pages) {
          setCurrentPage(0);
          if (container) {
            container.scrollLeft = 0;
          }
        }
      }
    };

    // Calculate on mount and when children or window size changes
    calculatePages();
    
    if (isBrowser) {
      window.addEventListener('resize', calculatePages);
      return () => {
        window.removeEventListener('resize', calculatePages);
      };
    }
  }, [children, currentPage, isClient]);

  // Go to previous page
  const goToPrevPage = () => {
    if (!isClient) return; // Skip during SSR
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
      scrollToPage(currentPage - 1);
    }
  };

  // Go to next page
  const goToNextPage = () => {
    if (!isClient) return; // Skip during SSR
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
      scrollToPage(currentPage + 1);
    }
  };

  // Scroll to a specific page
  const scrollToPage = (pageIndex) => {
    if (!isClient) return; // Skip during SSR
    if (containerRef.current) {
      const container = containerRef.current;
      const containerWidth = container.clientWidth;
      container.scrollTo({
        left: pageIndex * containerWidth,
        behavior: 'smooth'
      });
    }
  };

  // Handle scroll end to update current page
  const handleScroll = () => {
    if (!isClient) return; // Skip during SSR
    if (containerRef.current) {
      const container = containerRef.current;
      const containerWidth = container.clientWidth;
      const scrollPosition = container.scrollLeft;
      const newPage = Math.round(scrollPosition / containerWidth);
      
      if (newPage !== currentPage) {
        setCurrentPage(newPage);
      }
    }
  };

  // During SSR, render a simple container without navigation
  if (!isClient) {
    return (
      <div className={classNames(css.root, className)}>
        <div className={css.container}>
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(css.root, className, { [css.noOverflow]: !hasOverflow })}>
      {hasOverflow && currentPage > 0 && (
        <button 
          className={css.buttonLeft} 
          onClick={goToPrevPage}
          aria-label="Previous items"
        >
          <ChevronLeft />
        </button>
      )}
      <div 
        className={classNames(css.container, { 
          [css.noOverflow]: !hasOverflow,
          [css.paged]: hasOverflow
        })}
        ref={containerRef}
        onScroll={handleScroll}
      >
        {children}
      </div>
      {hasOverflow && currentPage < totalPages - 1 && (
        <button 
          className={css.buttonRight} 
          onClick={goToNextPage}
          aria-label="Next items"
        >
          <ChevronRight />
        </button>
      )}
    </div>
  );
};

HorizontalCarousel.defaultProps = {
  className: null,
};

HorizontalCarousel.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

export default HorizontalCarousel; 